import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  serverTimestamp,
  orderBy,
  limit as firestoreLimit,
  DocumentData,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import { COLLECTIONS } from "@/config/constants";
import { Drink, DrinkCategory } from "@/types";

/**
 * Drinks Service
 * Handles all Firestore operations related to drinks (subcollection)
 */
export const drinksService = {
  /**
   * Get drinks for a specific bar
   */
  getDrinksByBar: async (barId: string, limitCount = 50): Promise<Drink[]> => {
    try {
      const drinksRef = collection(
        db,
        COLLECTIONS.BARS,
        barId,
        COLLECTIONS.DRINKS
      );
      const q = query(
        drinksRef,
        orderBy("createdAt", "desc"),
        firestoreLimit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc: DocumentData) => ({
        id: doc.id,
        barId,
        ...doc.data(),
      })) as Drink[];
    } catch (error) {
      console.error("Error fetching drinks:", error);
      throw new Error("Fehler beim Laden der Getränke");
    }
  },

  /**
   * Get all drinks across all bars (for moderation)
   * Note: This requires a composite query or fetching from all bars
   */
  getAllDrinks: async (filters?: {
    approved?: boolean;
    category?: DrinkCategory;
    limit?: number;
  }): Promise<Drink[]> => {
    try {
      // This is a simplified version - in production, consider collection group query
      // or dedicated moderation collection
      const barsRef = collection(db, COLLECTIONS.BARS);
      const barsSnapshot = await getDocs(query(barsRef, firestoreLimit(100)));

      const allDrinks: Drink[] = [];

      for (const barDoc of barsSnapshot.docs) {
        const drinksRef = collection(
          db,
          COLLECTIONS.BARS,
          barDoc.id,
          COLLECTIONS.DRINKS
        );
        let q = query(drinksRef, orderBy("createdAt", "desc"));

        if (filters?.approved !== undefined) {
          q = query(q, where("approved", "==", filters.approved));
        }
        if (filters?.category) {
          q = query(q, where("category", "==", filters.category));
        }
        if (filters?.limit) {
          q = query(q, firestoreLimit(filters.limit));
        }

        const drinksSnapshot = await getDocs(q);
        const drinks = drinksSnapshot.docs.map((doc: DocumentData) => ({
          id: doc.id,
          barId: barDoc.id,
          ...doc.data(),
        })) as Drink[];

        allDrinks.push(...drinks);
      }

      return filters?.limit ? allDrinks.slice(0, filters.limit) : allDrinks;
    } catch (error) {
      console.error("Error fetching all drinks:", error);
      throw new Error("Fehler beim Laden aller Getränke");
    }
  },

  /**
   * Get drinks across bars in pages to avoid loading hundreds at once.
   * Processes barsPerPage bars per call, max limitPerBar drinks per bar.
   */
  getAllDrinksPaged: async (
    bars: { id: string }[],
    options: {
      barOffset?: number;
      barsPerPage?: number;
      limitPerBar?: number;
    } = {}
  ): Promise<{ drinks: Drink[]; hasMore: boolean; nextBarOffset: number }> => {
    const barOffset = options.barOffset ?? 0;
    const barsPerPage = options.barsPerPage ?? 15;
    const limitPerBar = options.limitPerBar ?? 15;

    const barsSlice = bars.slice(barOffset, barOffset + barsPerPage);
    const allDrinks: Drink[] = [];

    for (const bar of barsSlice) {
      try {
        const drinksRef = collection(
          db,
          COLLECTIONS.BARS,
          bar.id,
          COLLECTIONS.DRINKS
        );
        const q = query(
          drinksRef,
          orderBy("createdAt", "desc"),
          firestoreLimit(limitPerBar)
        );
        const snap = await getDocs(q);
        allDrinks.push(
          ...snap.docs.map((d: DocumentData) => ({
            id: d.id,
            barId: bar.id,
            ...d.data(),
          })) as Drink[]
        );
      } catch {
        // Skip bars with missing drinks subcollection
      }
    }

    return {
      drinks: allDrinks,
      hasMore: barOffset + barsPerPage < bars.length,
      nextBarOffset: barOffset + barsPerPage,
    };
  },

  /**
   * Get single drink
   */
  getDrink: async (barId: string, drinkId: string): Promise<Drink | null> => {
    try {
      const drinkRef = doc(
        db,
        COLLECTIONS.BARS,
        barId,
        COLLECTIONS.DRINKS,
        drinkId
      );
      const drinkDoc = await getDoc(drinkRef);

      if (!drinkDoc.exists()) {
        return null;
      }

      return {
        id: drinkDoc.id,
        barId,
        ...drinkDoc.data(),
      } as Drink;
    } catch (error) {
      console.error("Error fetching drink:", error);
      throw new Error("Fehler beim Laden des Getränks");
    }
  },

  /**
   * Approve drink
   */
  approveDrink: async (
    barId: string,
    drinkId: string,
    adminUid: string
  ): Promise<void> => {
    try {
      const drinkRef = doc(
        db,
        COLLECTIONS.BARS,
        barId,
        COLLECTIONS.DRINKS,
        drinkId
      );

      const current = await getDoc(drinkRef);
      const currentCount = current.exists()
        ? Number(current.data().verificationCount || 0)
        : 0;

      await updateDoc(drinkRef, {
        approved: true,
        status: "active",
        approvedAt: serverTimestamp(),
        approvedBy: adminUid,
        isVerified: true,
        verificationCount: Math.max(currentCount, 3),
        lastVerifiedAt: Timestamp.now(),
        verifiedByAdmin: adminUid,
        isAvailable: true,
        isBlocked: false,
        isHidden: false,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error approving drink:", error);
      throw new Error("Fehler beim Genehmigen des Getränks");
    }
  },

  /**
   * Update drink
   */
  updateDrink: async (
    barId: string,
    drinkId: string,
    updates: Partial<Drink>
  ): Promise<void> => {
    try {
      const drinkRef = doc(
        db,
        COLLECTIONS.BARS,
        barId,
        COLLECTIONS.DRINKS,
        drinkId
      );
      await updateDoc(drinkRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating drink:", error);
      throw new Error("Fehler beim Aktualisieren des Getränks");
    }
  },

  /**
   * Delete drink
   */
  deleteDrink: async (barId: string, drinkId: string): Promise<void> => {
    try {
      const drinkRef = doc(
        db,
        COLLECTIONS.BARS,
        barId,
        COLLECTIONS.DRINKS,
        drinkId
      );
      await deleteDoc(drinkRef);
    } catch (error) {
      console.error("Error deleting drink:", error);
      throw new Error("Fehler beim Löschen des Getränks");
    }
  },
};
